import type { BuildPriority, Environment, Platform } from '@hangar/shared';
import type { BuildCreateInput } from '@/api/endpoints';
import type { Guardrail, LaunchForm, Profile } from '@/api/schemas';

/*
 * Pure logic of the Run build form (no React, no network): what the profile / environment / platform pickers offer,
 * how a launch form is read, which messages and blocks apply, and how the request body is assembled. The form hook and
 * the components only wire these functions to state.
 */

export const SHA_RE = /^[0-9a-f]{40}$/;
export const PRIORITIES: BuildPriority[] = ['low', 'normal', 'high', 'urgent'];
export const ALL_PLATFORMS: Platform[] = ['android', 'ios'];
/** Launch-form keys with dedicated controls; anything else in `schema.properties` renders generically. */
const KNOWN_INPUTS = new Set(['track', 'iosDistribution', 'clearCache', 'autoSubmit', 'overrideFreeze']);
const CI_ONLY_MESSAGE = 'Hangar cannot start this profile: it builds from your CI with hangar build';

export type Inputs = Record<string, unknown>;
export type SchemaProp = { type?: string; enum?: unknown[]; title?: string; description?: string; default?: unknown };
/** The profile rows that share one slug (the API stores one row per platform). */
export interface ProfileGroup {
  slug: string;
  name: string;
  list: Profile[];
}
/** An environment of the project, `bound` = the selected profile has a binding for it (or has none at all). */
export interface EnvChoice extends Environment {
  bound: boolean;
}

/* ---------- Target: project / app / profile / environment / platforms ---------- */

/** The chosen project, else the preset's, else the first one. */
export function pickProject<P extends { id: string; slug: string }>(projects: P[] | undefined, projectId: string, presetSlug?: string): P | undefined {
  return projects?.find((p) => p.id === projectId) ?? projects?.find((p) => p.slug === presetSlug) ?? projects?.[0];
}

/** The chosen app, else the first one. */
export function pickApp<A extends { id: string }>(apps: A[] | undefined, appId: string): A | undefined {
  return apps?.find((a) => a.id === appId) ?? apps?.[0];
}

/** Profiles group by slug (one row per platform in the API); the picker shows slugs and which platforms exist. */
export function groupProfiles(profiles: Profile[] | undefined): ProfileGroup[] {
  const map = new Map<string, Profile[]>();
  for (const p of profiles ?? []) map.set(p.slug, [...(map.get(p.slug) ?? []), p]);
  return [...map.entries()].map(([slug, list]) => ({ slug, name: list[0]!.name, list }));
}

/** The chosen group, else the one holding the preset profile, else the first one. */
export function pickGroup(groups: ProfileGroup[], profileSlug: string, presetProfileId?: string): ProfileGroup | undefined {
  return groups.find((g) => g.slug === profileSlug) ?? groups.find((g) => g.list.some((p) => p.id === presetProfileId)) ?? groups[0];
}

/** Platforms the group has a profile for, in the fixed android → ios order. */
export function availablePlatformsOf(group: ProfileGroup | undefined): Platform[] {
  return ALL_PLATFORMS.filter((pl) => group?.list.some((p) => p.platform === pl));
}

/** Selected platforms that the profile group still offers (a profile switch drops the rest). */
export function selectPlatforms(platforms: Platform[], available: Platform[]): Platform[] {
  return platforms.filter((p) => available.includes(p));
}

/** Checkbox toggle: the platform leaves the selection when it is on, joins it otherwise. */
export function togglePlatform(selected: Platform[], platform: Platform): Platform[] {
  return selected.includes(platform) ? selected.filter((x) => x !== platform) : [...selected, platform];
}

/** Non-archived environments by sort order; one is `bound` when the profile has a binding for it (no bindings = all). */
export function environmentChoices(environments: Environment[] | undefined, group: ProfileGroup | undefined): EnvChoice[] {
  const live = [...(environments ?? [])].filter((e) => !e.archivedAt).sort((a, b) => a.sortOrder - b.sortOrder);
  const bound = new Set(group?.list.flatMap((p) => p.environments.map((b) => b.environmentId)) ?? []);
  return live.map((e) => ({ ...e, bound: bound.size === 0 || bound.has(e.id) }));
}

/** The requested environment when it is bound, else the first bound one. */
export function pickEnvironment(choices: EnvChoice[], slug: string): EnvChoice | undefined {
  return choices.find((e) => e.slug === slug && e.bound) ?? choices.find((e) => e.bound);
}

/** The environment's first literal (non-glob) branch pattern, `master` when there is none. */
export function defaultBranch(env: Pick<Environment, 'allowedBranchPatterns'>): string {
  return env.allowedBranchPatterns.find((p) => !p.includes('*')) ?? 'master';
}

/** The profile the launch form is read from: the first selected platform's (or the first available one's). */
export function primaryProfileOf(group: ProfileGroup | undefined, selected: Platform[], available: Platform[]): Profile | undefined {
  return group?.list.find((p) => p.platform === (selected[0] ?? available[0]));
}

/** The `platform` argument of the launch-form request: `all` for several platforms. */
export function launchPlatformArg(selected: Platform[]): Platform | 'all' | undefined {
  return selected.length > 1 ? 'all' : selected[0];
}

/** True while any of the queries that feed the pickers (projects, apps, profiles) has no data yet. */
export function isLoadingBasics(queries: Array<{ isLoading: boolean; data: unknown }>): boolean {
  return queries.some((q) => q.isLoading && !q.data);
}

/** Whether Hangar's own runner is allowed by every profile of the group (no group = nothing to warn about). */
export function isRunnerAllowed(group: ProfileGroup | undefined): boolean {
  return !group || group.list.every((p) => p.executionModes.includes('hangar_runner'));
}

/* ---------- Launch form ---------- */

export interface LaunchSchemaView {
  props: Record<string, SchemaProp>;
  tracks: string[];
  iosDistributions: string[];
  customInputs: Array<[string, SchemaProp]>;
}

const enumOf = (prop: SchemaProp | undefined): string[] => (Array.isArray(prop?.enum) ? (prop.enum as string[]) : []);

/** Reads the JSON Schema of a launch form: its properties, the track and iOS distribution choices, and the generic inputs. */
export function readLaunchSchema(launch: Pick<LaunchForm, 'schema' | 'allowedTracks'> | undefined): LaunchSchemaView {
  const props = (launch?.schema.properties ?? {}) as Record<string, SchemaProp>;
  return {
    props,
    tracks: launch?.allowedTracks ?? enumOf(props.track),
    iosDistributions: enumOf(props.iosDistribution),
    customInputs: Object.entries(props).filter(([k]) => !KNOWN_INPUTS.has(k)),
  };
}

/** Guardrails the server attached to one form field. */
export function guardrailsFor(guardrails: Guardrail[] | undefined, field: string): Guardrail[] {
  return (guardrails ?? []).filter((g) => g.field === field);
}

/** Guardrails shown in the aside: those without a field and every `block`. */
export function generalGuardrailsOf(guardrails: Guardrail[] | undefined): Guardrail[] {
  return (guardrails ?? []).filter((g) => !g.field || g.level === 'block');
}

/** What a number or text input reports: an empty number is `undefined` (dropped from the request), anything else its value. */
export function inputValueOf(raw: string, numeric: boolean): unknown {
  if (!numeric) return raw;
  return raw === '' ? undefined : Number(raw);
}

/** Launch defaults under the user's edits (what the user already typed wins). */
export function mergeLaunchDefaults(prev: Inputs, defaults: Inputs): Inputs {
  return { ...defaults, ...prev };
}

/** `autoSubmit` takes the launch default only while the user has not decided (`null`) and the default is a boolean. */
export function mergeAutoSubmit(prev: boolean | null, defaults: Inputs): boolean | null {
  return prev === null && typeof defaults.autoSubmit === 'boolean' ? defaults.autoSubmit : prev;
}

/** The "Submit after build" switch shows when the schema has the property or the user/defaults already set it. */
export function showAutoSubmit(props: Record<string, SchemaProp>, autoSubmit: boolean | null): boolean {
  return 'autoSubmit' in props || autoSubmit !== null;
}

/** The track a submit would go to, as the switch's description. */
export function autoSubmitDescription(inputs: Inputs): string | undefined {
  return typeof inputs.track === 'string' ? `To ${inputs.track}` : undefined;
}

/**
 * The launch form decides first: a CI-only profile (`startable: false`, HGR-EXECUTION-MODE) or any `block` guardrail
 * (a branch outside the environment's patterns, an invalid Bitrise target, a protected environment the caller or the app
 * cannot build on) cannot be submitted; then the dry-run plan. A form that is not startable says why with its own `block`
 * guardrail (the CI message is only the fallback), so a protected environment never reads as "builds from your CI".
 */
export function launchBlockOf(launch: Pick<LaunchForm, 'startable' | 'guardrails'> | undefined): string | undefined {
  if (!launch) return undefined;
  const block = launch.guardrails.find((g) => g.level === 'block')?.message;
  if (launch.startable === false) return launch.guardrails.find((g) => g.code === 'HGR-EXECUTION-MODE')?.message ?? block ?? CI_ONLY_MESSAGE;
  return block;
}

/** A launch block, or a dry-run plan with a blocked platform, disables Run. */
export function isBlocked(launchBlock: string | undefined, plan: Array<{ blocked: boolean }> | undefined): boolean {
  return Boolean(launchBlock) || (plan?.some((p) => p.blocked) ?? false);
}

/** How a plan check renders: passed is green, failed/error red, anything else (skipped, warning…) amber. */
export function checkTone(status: string): 'passed' | 'failed' | 'other' {
  if (status === 'passed') return 'passed';
  return status === 'failed' || status === 'error' ? 'failed' : 'other';
}

/* ---------- Validation ---------- */

export interface LocalErrors {
  app?: string;
  profile?: string;
  environment?: string;
  platforms?: string;
  branch?: string;
  commitSha?: string;
}
export type ErrorField = keyof LocalErrors;
export interface ValidationInput {
  hasApp: boolean;
  hasProfile: boolean;
  hasEnvironment: boolean;
  platformCount: number;
  branch: string;
  commitSha: string;
}

/** A commit SHA is empty (branch head) or exactly 40 lowercase hex characters. */
export function isValidCommitSha(sha: string): boolean {
  const s = sha.trim();
  return !s || SHA_RE.test(s);
}

/** The messages shown before the server is asked: one entry per field that is wrong right now. */
export function validateLocal(v: ValidationInput): LocalErrors {
  return {
    app: !v.hasApp ? 'Pick an app' : undefined,
    profile: !v.hasProfile ? 'The app has no build profile yet — create one in Apps & profiles' : undefined,
    environment: !v.hasEnvironment ? 'Pick an environment' : undefined,
    platforms: v.platformCount === 0 ? 'Pick at least one platform' : undefined,
    branch: !v.branch.trim() ? 'Branch is required' : undefined,
    commitSha: !isValidCommitSha(v.commitSha) ? 'A full 40-character commit SHA, or leave it empty for the branch head' : undefined,
  };
}

export const hasLocalErrors = (local: LocalErrors): boolean => Object.values(local).some(Boolean);

/**
 * The message of a field: the server's (by field, git for branch/commit) wins; a local one shows after the first
 * submit, or at once when the field validates live.
 */
export function errorOf(field: ErrorField, server: Record<string, string>, local: LocalErrors, showLocal: boolean): string | undefined {
  const serverKey = field === 'branch' || field === 'commitSha' ? 'git' : field;
  return server[field] ?? server[serverKey] ?? (showLocal ? local[field] : undefined);
}

/* ---------- Request body ---------- */

export interface PayloadInput {
  appSlug: string | undefined;
  projectSlug: string | undefined;
  profileSlug: string | undefined;
  environmentSlug: string | undefined;
  platforms: Platform[];
  branch: string;
  commitSha: string;
  inputs: Inputs;
  autoSubmit: boolean | null;
  priority: BuildPriority;
  message: string;
  runnerAllowed: boolean;
  dryRun: boolean;
}

/** The inputs sent to the API: `autoSubmit` travels apart, and unset or empty values are dropped. */
export function mergeInputs(inputs: Inputs): Inputs {
  const merged: Inputs = {};
  for (const [k, v] of Object.entries(inputs)) if (k !== 'autoSubmit' && v !== undefined && v !== '') merged[k] = v;
  return merged;
}

const gitOf = (branch: string, commitSha: string) => ({ branch: branch.trim(), ...(commitSha.trim() ? { commitSha: commitSha.trim() } : {}) });

const autoSubmitOf = (autoSubmit: boolean | null, inputs: Inputs) => {
  if (autoSubmit === null) return {};
  const track = typeof inputs.track === 'string' ? inputs.track : undefined;
  return { autoSubmit: { enabled: autoSubmit, ...(autoSubmit && track ? { track } : {}) } };
};

const executionOf = (runnerAllowed: boolean) => (runnerAllowed ? { mode: 'hangar_runner' as const, fallbackToRunner: true } : { fallbackToRunner: true });

/** Whether the form picked an app, a profile, an environment and at least one platform. */
const hasTarget = (p: PayloadInput): boolean => p.appSlug !== undefined && p.profileSlug !== undefined && p.environmentSlug !== undefined && p.platforms.length > 0;

/** `null` when the target is incomplete; otherwise the exact `POST /builds` body (`dryRun` makes it a preview). */
export function buildPayload(p: PayloadInput): BuildCreateInput | null {
  if (!hasTarget(p)) return null;
  return {
    app: p.appSlug!,
    project: p.projectSlug,
    platforms: p.platforms,
    profile: p.profileSlug!,
    environment: p.environmentSlug!,
    git: gitOf(p.branch, p.commitSha),
    inputs: mergeInputs(p.inputs),
    ...autoSubmitOf(p.autoSubmit, p.inputs),
    priority: p.priority,
    execution: executionOf(p.runnerAllowed),
    ...(p.message.trim() ? { message: p.message.trim() } : {}),
    dryRun: p.dryRun,
  };
}
